import { AmountCurrency, PriceRecurringInterval } from 'database'
import React, { useMemo } from 'react'
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Button,
  cn,
  AccordionItemRef,
  AccordionItemProps,
} from 'ui'

export const PricingCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      {...props}
      className={cn(
        'border-b-0 w-full border border-primary rounded-lg bg-neutral-900 pt-5',
        className
      )}
      ref={ref}
    />
  )
})

export const PricingCardHeader = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div className={cn('w-full px-5', className)}>
      <div className='flex flex-col w-full'>{children}</div>
    </div>
  )
}

export const PricingCardInfo = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cn('flex flex-row justify-between items-center', className)}
    >
      {children}
    </div>
  )
}

export const PricingCardFeatureLineDescription = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <p className={cn('', className)}>{children}</p>
}

export const PricingCardFeatureLineIcon = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div className={cn('[&>*]:w-6 [&>*]:h-6 text-primary', className)}>
      {children}
    </div>
  )
}

export const PricingCardFeatureLine = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <div className='flex flex-row items-start space-x-4 text-left'>
      {children}
    </div>
  )
}

export const PricingCardButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  return (
    <Button
      variant={'outline-secondary'}
      className={cn('w-full text-primary', className)}
      {...props}
      ref={ref}
    />
  )
})

export const PricingCardFooter = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <div className={cn(className)}>{children}</div>
}

export const PricingCardFeatures = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return <div className='space-y-4 px-5'>{children}</div>
}

export const PricingCardPrice = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div className={cn('w-full flex flex-row items-center h-8', className)}>
      {children}
    </div>
  )
}

export const PricingCardPriceAmountFormatted = ({
  children,
  currency,
  className,
}: {
  children: string
  currency: AmountCurrency
  className?: string
}) => {
  const amount = useMemo(
    () =>
      new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency,
      }).format(+children),
    [children]
  )

  return (
    <PricingCardPriceAmount className={className}>
      {amount}
    </PricingCardPriceAmount>
  )
}

export const PricingCardPriceAmount = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <h2 className={cn('text-[#a4d035] text-[1.4rem] font-bold', className)}>
      {children}
    </h2>
  )
}

export const PricingCardPricePeriod = ({
  period,
  className,
}: {
  period: PriceRecurringInterval
  className?: string
}) => {
  const periodLabel = period === 'month' ? 'por\nmes' : 'por\naño'

  return (
    <p
      className={cn(
        'font-light text-[#a4d035] text-xs leading-4 ml-2',
        className
      )}
    >
      {periodLabel}
    </p>
  )
}

export const PricingCardContent = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <div className={cn('space-y-8', className)}>{children}</div>
}

export const PricingCardTitle = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <h2 className={cn('text-lg font-bold', className)}>{children}</h2>
}

export const PricingCardTitleIcon = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div className={cn('[&>*]:h-8 [&>*]:w-8 text-lime-strong', className)}>
      {children}
    </div>
  )
}
